import React from "react";
import { lightenColor } from "./chartTheme";
import { ChartDataPoint } from "../types";
import { extractValue } from "../utils/chartDotHelpers";

const DOT_VISIBLE_RADIUS = 8;
const DOT_STROKE_WIDTH = 3;
const DOT_HIT_RADIUS = 18;
const FILL_LIGHTEN_AMOUNT = 0.3;

/**
 * Props for CustomDot component
 */
interface CustomDotProps {
  cx: number;
  cy: number;
  payload: ChartDataPoint;
  dataKey?: string;
  viewMode?: string;
  getColorFn: (value: number) => string;
}

/**
 * Custom dot component for chart data points
 * Used to render individual data points with dynamic colors based on value
 */
export const CustomDot: React.FC<CustomDotProps> = ({
  cx,
  cy,
  payload,
  dataKey,
  viewMode,
  getColorFn,
}) => {
  // Filter dots based on view mode (for blood pressure chart)
  if (viewMode && dataKey) {
    if (viewMode === "systolic" && dataKey === "diastolic") {
      return null;
    }
    if (viewMode === "diastolic" && dataKey === "systolic") {
      return null;
    }
  }

  const value = extractValue(payload, dataKey);

  // Safety check: don't render if value is invalid
  if (value === null || !Number.isFinite(value)) {
    return null;
  }

  const strokeColor = getColorFn(value);
  const fillColor = lightenColor(strokeColor, FILL_LIGHTEN_AMOUNT);

  return (
    <TrendDotCircleGroup
      cx={cx}
      cy={cy}
      fillColor={fillColor}
      strokeColor={strokeColor}
    />
  );
};

function TrendDotCircleGroup({
  cx,
  cy,
  strokeColor,
  fillColor,
}: {
  cx: number;
  cy: number;
  strokeColor: string;
  fillColor: string;
}) {
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={DOT_HIT_RADIUS}
        fill="transparent"
        stroke="none"
        pointerEvents="all"
      />
      <circle
        cx={cx}
        cy={cy}
        r={DOT_VISIBLE_RADIUS}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={DOT_STROKE_WIDTH}
        pointerEvents="none"
      />
    </g>
  );
}

/**
 * Props for CustomActiveDot component
 */
interface CustomActiveDotProps {
  cx: number;
  cy: number;
  payload: ChartDataPoint;
  dataKey?: string;
  getColorFn: (value: number) => string;
}

/**
 * Custom active dot component for highlighted/hovered data points
 * Same as CustomDot but used when a point is actively selected
 */
export const CustomActiveDot: React.FC<CustomActiveDotProps> = ({
  cx,
  cy,
  payload,
  dataKey,
  getColorFn,
}) => {
  const value = extractValue(payload, dataKey);

  // Safety check: don't render if value is invalid
  if (value === null || !Number.isFinite(value)) {
    return null;
  }

  const strokeColor = getColorFn(value);
  const fillColor = lightenColor(strokeColor, FILL_LIGHTEN_AMOUNT);

  return (
    <TrendDotCircleGroup
      cx={cx}
      cy={cy}
      fillColor={fillColor}
      strokeColor={strokeColor}
    />
  );
};
